import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap, type ApiClient } from "./client";
import {
  invalidateProjectHistory,
  queryKeys,
  type ProjectOverview,
  type ProjectSummary,
} from "./hooks";

/**
 * Freezing the current draft into an immutable preview candidate (#260).
 *
 * Aliases into the generated contract, like `hooks.ts` and `library.ts`: change
 * the contract, regenerate, and these names either still resolve or fail to
 * compile.
 */
export type CandidateCreated = components["schemas"]["CandidateCreated"];
export type FreezeCandidateRequest =
  components["schemas"]["FreezeCandidateRequest"];

/**
 * The three shapes #261 adds, plus the outcome signal.
 *
 * Aliased, never hand-copied, for the same reason as everything above:
 * `CreatePreviewSessionRequest` is `additionalProperties: false` with all three
 * fields required, so the TYPE is what enforces "exactly the three contract
 * fields" and the comment beside the call site is only the explanation.
 */
export type CandidateList = components["schemas"]["CandidateList"];
export type CandidateSummary = components["schemas"]["CandidateSummary"];
/**
 * The server's currency reading of a frozen candidate: whether the immutable
 * artifact still represents the Project's CURRENT effective configuration
 * (draft version, adoption pin-set digest and base identity all unchanged
 * since the freeze, and no later freeze). Derived server-side against the
 * Project's current rows; the browser never infers it from `validity`, list
 * position or expiry — which is the inference that launched a candidate whose
 * adoptions had been removed as if it were current.
 */
export type CandidateFreshness =
  | components["schemas"]["CandidateCurrent"]
  | components["schemas"]["CandidateSuperseded"];
export type CandidateSupersessionReason =
  components["schemas"]["CandidateSupersessionReason"];
export type PreviewPatientChoice =
  components["schemas"]["PreviewPatientChoice"];
export type PreviewPatientChoices =
  components["schemas"]["PreviewPatientChoices"];
export type CreatePreviewSessionRequest =
  components["schemas"]["CreatePreviewSessionRequest"];
export type PreviewSessionCreated =
  components["schemas"]["PreviewSessionCreated"];
export type PreviewSessionOutcome =
  components["schemas"]["PreviewSessionOutcome"];

/**
 * What #338 sends to move a Project's preview target.
 *
 * Aliased like everything above: `additionalProperties: false` with both fields
 * required, so the TYPE is what enforces "the compare-and-set token travels with
 * the choice" and no request can carry the target without the revision it was
 * chosen against.
 */
export type ChangePreviewTargetRequest =
  components["schemas"]["ChangePreviewTargetRequest"];

/**
 * What #430 serves for ONE frozen candidate, aliased like everything above.
 *
 * `FrozenCandidateContents` carries the whole `CandidateSummary` unchanged and
 * adds `families` and `integrity` — the contract's own "create, list and
 * inspect never disagree about one candidate". Aliasing rather than restating
 * it is what keeps that true here: a field the service stops serving fails to
 * compile at the surface that renders it instead of rendering as `undefined`.
 */
export type FrozenCandidateContents =
  components["schemas"]["FrozenCandidateContents"];
export type CandidateContentFamily =
  components["schemas"]["CandidateContentFamily"];
export type CandidateContentFile =
  components["schemas"]["CandidateContentFile"];
export type FrozenContentsIntegrity =
  components["schemas"]["FrozenContentsIntegrity"];

/**
 * What the browser sends to freeze a candidate, and nothing else.
 *
 * The whole request is the path `projectId` plus this body. There is no tenant
 * id, no endpoint, and no credential in it: the Project's preview target is
 * read server-side from the Project row (#202 PR C), `editorServiceBaseUrl` is
 * deploy-time configuration the client is constructed with, and the single
 * outbound header is the bearer `authorization` that `api/client.ts` attaches
 * to every request. `CandidateFreezeControl.test.tsx` records an actual request
 * and asserts exactly that.
 *
 * `additionalProperties: false` on the contract schema means a fourth field
 * would be refused by the service rather than ignored, so the type is the
 * enforcement and this comment is only the explanation.
 */
export function useFreezeCandidateMutation(
  projectId: string,
): UseMutationResult<CandidateCreated, unknown, FreezeCandidateRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body: FreezeCandidateRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/preview-candidates", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    onSuccess() {
      // A freeze advances `DraftSummary.latestCandidateId`, which the Overview
      // renders — and, since #261, it adds a row to the candidate listing the
      // selector below reads. It also commits the immutable revision-frozen
      // History row. All three projections are stale once this returns.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.previewCandidates(projectId),
      });
      invalidateProjectHistory(queryClient, projectId);
    },
    // The caller mints an `idempotencyKey` at intent, so a retry the CLINICIAN
    // asks for is safe. An automatic one is still refused: a retry that races
    // its own first attempt would be indistinguishable from the lost-response
    // case the key exists to make safe, and the contract's 409
    // `idempotency_conflict` is a state a human should see rather than one a
    // background retry should churn on.
    retry: false,
  });
}

/**
 * Why a freeze did not produce a candidate.
 *
 * Kinds only — the sentences live with the surface that renders them
 * (`ui/preview/CandidateFreezeControl.tsx`), keyed by these members with
 * `satisfies`, so a new kind fails the build rather than rendering blank. This
 * is the split `useCandidateCreationGate` does not make, and the reason is that
 * its `detail` strings are a documented seam other components consume verbatim;
 * these are not.
 */
export type FreezeFailure =
  /** 422 `preview_target_not_enabled` — the Project's target lost its credential. */
  | { kind: "preview_target" }
  /** 422 `validation_failed` — the effective configuration is not clean. (#497 retired the
   * "no saved edits" cause: that state is freezable on its own terms.) */
  | { kind: "not_validated" }
  /** 409 `stale_draft_version` — the draft moved after it was reviewed. */
  | { kind: "stale_draft" }
  /** 409 `idempotency_conflict` — this attempt's key already froze another version. */
  | { kind: "idempotency_conflict" }
  /** 409 `stale_adoptions` — the adopted content changed while the engine validated. */
  | { kind: "stale_adoptions" }
  | { kind: "denied" }
  /** 501 — the endpoint is not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  /** Anything else, carrying the machine-readable code and the request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

/**
 * Classifies a failed freeze by what came back, not by status alone.
 *
 * Two 422s and two 409s share a status each and mean different things, and the
 * recovery differs every time: re-check the target listing, validate again,
 * re-read the draft, or start a fresh attempt. Reading `error.code` is how the
 * contract says to tell them apart — `createPreviewCandidate` declares an
 * `ErrorEnvelope` on both statuses, unlike `runValidation`'s body-less 409.
 *
 * Still deliberately NOT handled here: 409 `stale_preview_target_revision`.
 * #262 built the guided recovery for that code on the two surfaces that can
 * actually produce it — session creation and the retarget — and this is not one
 * of them: `createPreviewCandidate` does not declare the code, and a candidate
 * is target-neutral, so a branch here would be dead code carrying copy for a
 * refusal this route cannot answer with. It falls through to `error` with its
 * code visible, which is the honest treatment of a refusal the contract does
 * not describe.
 */
export function classifyFreezeFailure(error: unknown): FreezeFailure {
  if (error instanceof ApiError) {
    if (error.code === "preview_target_not_enabled") {
      return { kind: "preview_target" };
    }
    if (error.isForbidden || error.isUnauthenticated) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.isConflict) {
      if (error.code === "idempotency_conflict") {
        return { kind: "idempotency_conflict" };
      }
      if (error.code === "stale_draft_version") {
        return { kind: "stale_draft" };
      }
      if (error.code === "stale_adoptions") {
        return { kind: "stale_adoptions" };
      }
    }
    if (error.status === 422) {
      return { kind: "not_validated" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // A thrown non-ApiError is a network or runtime failure: no status, no code.
  return {
    kind: "error",
    code: "upstream_unavailable",
    requestId: undefined,
  };
}

/**
 * The project's frozen candidates, newest first (#261).
 *
 * The first browser consumer of `listPreviewCandidates`, and a deliberate,
 * owner-approved widening of #261's written scope: without it the journey does
 * not survive a page reload, because `CandidateFreezeControl` can only name a
 * candidate it froze in THIS session. A clinician who freezes, reloads, and
 * comes back to launch would otherwise have to freeze again to learn the id of
 * what they already have.
 *
 * The request is the path `projectId` and nothing else. Every fact the picker
 * shows — identity, freeze evidence, expiry, eligibility — is server-derived
 * and stored at freeze, so nothing here recomputes or infers any of it.
 */
export function usePreviewCandidatesQuery(
  projectId: string,
): UseQueryResult<CandidateList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewCandidates(projectId),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/preview-candidates", {
          params: { path: { projectId } },
        }),
      );
    },
    enabled: projectId.length > 0,
    // Always re-read on mount. Each item carries the server's `freshness`
    // reading — whether the candidate still represents the Project's CURRENT
    // effective configuration — and that reading is what the Preview modal
    // launches on. A snapshot up to `staleTime` old could still say `current`
    // about a candidate whose adoptions another tab, or another clinician,
    // removed since, and the modal would launch it as the current
    // configuration. The writes this app makes invalidate the key as well;
    // this is the half that covers writes it did not make. The listing is
    // one small read on a modal the clinician opens deliberately.
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * The exact frozen contents of ONE candidate (#430, consumed by #435).
 *
 * The request is the two path parameters and nothing else. What comes back is
 * the archive the freeze stored, hash-verified against the row before a byte is
 * read — never the current draft and never a live recomposition — which is the
 * property the review surface rests on: editing the draft after the freeze
 * cannot change this response.
 *
 * `candidateId` is nullable and the query is disabled while it is null, because
 * the caller's candidate arrives from a Project Overview read that may not have
 * resolved yet. A hook that quietly substituted "the latest one" would be the
 * silent retarget #435 exists to prevent; there is nothing to inspect until a
 * caller says which candidate, so there is nothing to fetch.
 *
 * `staleTime: Infinity`, which is unusual here and is the honest setting rather
 * than an optimisation. Every other query in this file describes something that
 * moves. A candidate does not: its identity IS its content hash, the archive is
 * immutable by construction, and the service verifies both before answering. A
 * background refetch could therefore only ever return the same bytes, and a
 * refetch that returned different ones would be a fact this application must
 * not paper over. The clinician-driven retry below is a real refetch and stays
 * available; only the automatic re-read is switched off.
 *
 * Retries are left to the shared policy in `queryClient.ts`, which refuses to
 * retry 401/403/404/422/501 — every terminal refusal this route declares. The
 * 422 is the one that had to be added there, and this route is why: it means
 * the candidate has expired or is not this project's, and since the response is
 * about ONE immutable candidate, the identical read can only be refused
 * identically. `CandidateReview` withholds the manual retry on the same fact,
 * so a clinician is neither made to wait through two automatic attempts nor
 * offered a button that cannot work.
 *
 * The 503 keeps both retries, and correctly: an integrity failure is a property
 * of the stored archive, but 503 is also what a store this route cannot reach
 * answers with, and those two are indistinguishable from here — so the second
 * attempt is the one that can succeed.
 */
export function useCandidateContentsQuery(
  projectId: string,
  candidateId: string | null,
): UseQueryResult<FrozenCandidateContents> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewCandidateContents(projectId, candidateId ?? ""),
    async queryFn() {
      return unwrap(
        await client.GET(
          /*
           * The FIRST browser call site in this application whose Editor
           * Service path template names a candidate, which is why the ADR 0001
           * lint heuristic has never fired on one before. It is suppressed
           * here, once, rather than widened for everyone.
           *
           * ADR 0001 forbids a candidate identifier in a BROWSER URL — the
           * address bar, a link, a route parameter. This is an API request
           * path, and the contract puts `candidateId` in it deliberately: the
           * same document's `projectId` parameter carries the sentence "Never a
           * patient or candidate identifier — ADR 0001 forbids those in any
           * browser URL", so the two are distinguished in the contract itself.
           * `client.GET` takes this string as a TEMPLATE and openapi-fetch
           * substitutes `params.path` into it; nothing here reaches
           * `react-router`, `history`, or an anchor.
           *
           * The rule's own comment says it "catches the obvious authoring
           * mistakes early" and points at `src/invariants` for the executable
           * form. That form — `routes.test.ts`, which walks the real route
           * table and refuses `/candidate/i` in any path or parameter — is
           * untouched by this line and still enforces the actual prohibition.
           */
          // eslint-disable-next-line no-restricted-syntax
          "/v1/projects/{projectId}/preview-candidates/{candidateId}/contents",
          // Non-null by the `enabled` guard below: TanStack Query never runs a
          // disabled query's `queryFn`, and the empty string in the key above
          // is only there because a key may not contain `null`.
          { params: { path: { projectId, candidateId: candidateId ?? "" } } },
        ),
      );
    },
    enabled: projectId.length > 0 && (candidateId ?? "").length > 0,
    staleTime: Infinity,
  });
}

/**
 * Why one candidate's frozen contents could not be read.
 *
 * Kinds only, exactly as `FreezeFailure` and `SessionFailure` above: the
 * sentences live with the surface that renders them, keyed by these members
 * with `satisfies`, so a new kind fails the build rather than rendering blank.
 *
 * `integrity` is ONE member for the whole 5xx family, and the collapse is the
 * route's rather than this file's. Every arm that cannot prove the archive —
 * the stored object failing its digest, a length that disagrees with the row, a
 * refused or unreadable tar entry, an unparseable manifest, a manifest naming
 * another content hash — raises the same `503 upstream_unavailable`, and so
 * does a store that is simply away. `error.code` is identical across all of
 * them, and so is the transport's own body-less default. Splitting them here
 * would be this application claiming a distinction the envelope refuses to make
 * — `classifyPatientListingFailure` declines the same invitation for the same
 * reason — and it would have to guess in the one direction that matters: a
 * candidate whose bytes did not verify must never be accepted. So the kind is
 * one, its sentence says only what is known, and acceptance stays blocked
 * whichever of the two it was. Fail closed, then let the clinician retry.
 */
export type CandidateContentsFailure =
  /** 422 `validation_failed` on `candidateId` — expired, or unknown here. */
  | { kind: "candidate_unusable" }
  /** 502/503/504 — the frozen archive could not be verified and read. */
  | { kind: "integrity" }
  /** 401/403/404 — not this caller's to read, or not there. Indistinguishable. */
  | { kind: "denied" }
  /** 501 — the endpoint is not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  /** Anything else, carrying the machine-readable code and the request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

/**
 * Classifies a failed contents read by what came back, not by status alone.
 *
 * 404 is folded into `denied` on the contract's own instruction: "denials are
 * the project's own (absent, other-project and other-environment candidates
 * answer identically)". Telling a clinician which of those it was would be this
 * application inventing a distinction the service refuses to make — the same
 * honesty `classifySessionFailure` shows in the name `candidate_unusable`,
 * which is why the 422 here carries that name too.
 *
 * 401 lands on `denied` as well, matching `classifyPatientListingFailure`, and
 * NOT on the expired-session branch `QueryErrorState` has. This surface renders
 * inside a Project page that is itself behind that gate: a session that had
 * expired would have taken the whole page, not one panel of it, so a
 * sign-in-again control here would be a second recovery for a state the shell
 * has already handled.
 */
export function classifyCandidateContentsFailure(
  error: unknown,
): CandidateContentsFailure {
  if (error instanceof ApiError) {
    if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.status === 422) {
      return { kind: "candidate_unusable" };
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return { kind: "integrity" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // A thrown non-ApiError is a network or runtime failure: no status, no code.
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}

/**
 * The synthetic test patients the project's OWN preview target offers.
 *
 * Deployment-and-upstream state rather than project state, so it is NOT cached
 * across the session: `staleTime: 0`, the same reasoning as
 * `usePreviewTargetsQuery`. A fixture can be deactivated on the Tenant Liv side
 * without anything here being deployed, and a stale list offers a choice the
 * session creation then refuses `422 patientRef`.
 *
 * `retry: false`, unlike an ordinary read. The documented failure is `503
 * upstream_unavailable`, which is ONE code for three upstream conditions —
 * a missing credential, a refused one, and a preview-disabled environment —
 * and none of the three is fixed by asking again a second later. The surface
 * renders the state and offers the clinician the retry instead, which is the
 * one retry that can coincide with somebody provisioning a credential.
 */
export function usePreviewPatientsQuery(
  projectId: string,
): UseQueryResult<PreviewPatientChoices> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewPatients(projectId),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/preview-patients", {
          params: { path: { projectId } },
        }),
      );
    },
    enabled: projectId.length > 0,
    staleTime: 0,
    retry: false,
  });
}

/**
 * Creates the preview session and returns the opaque Liv link.
 *
 * The whole request is the path `projectId` plus `CreatePreviewSessionRequest`,
 * which the contract declares `additionalProperties: false` with all three
 * fields required. There is no tenant id, no endpoint, no environment and no
 * credential in it: the Project's preview target is read server-side from the
 * Project row (#19's provenance rule), the base URL is deploy-time
 * configuration the client was constructed with, and the single outbound header
 * is the bearer `api/client.ts` attaches to every request.
 *
 * `retry: false`, and this one is load-bearing rather than conventional. The
 * contract states the route carries NO idempotency key on purpose — a replay
 * would have to persist the recorded `previewUrl`, and that URL is itself a
 * single-use credential — so an automatic second attempt would mint a second
 * dispatch and a second live link that nobody opens.
 */
export function useCreatePreviewSessionMutation(
  projectId: string,
): UseMutationResult<PreviewSessionCreated, unknown, CreatePreviewSessionRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body: CreatePreviewSessionRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/preview-sessions", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    // The run may become the qualifying preview the Lab's Accept button reads.
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      });
    },
    retry: false,
  });
}

/**
 * Posts the outcome signal, and never lets it affect the launch.
 *
 * Owner decision, 2026-08-13, settling the question the clarity pass left open:
 * #261 reports `created` here rather than leaving the whole telemetry surface
 * to #262 — which then added the refusals, through `sessionFailureOutcome`
 * below. `PreviewSessionOutcome` carries no patient and no candidate identifier
 * by construction — the contract's own sentence — so there is nothing here to
 * redact, on either outcome.
 *
 * Deliberately NOT a mutation, and deliberately not awaited by any caller. A
 * failed signal is not a failed launch: the link is already open in another
 * tab and the token is already spent, so an error state about the reporting of
 * that fact would describe something the clinician neither caused nor can fix.
 * The rejection is swallowed for the same reason — the alternative is an
 * unhandled rejection in the console for an outcome nobody acts on.
 *
 * `environment` comes from the runtime configuration, whose enum (`local | dev
 * | staging`) is a strict subset of the telemetry enum (`local | dev | qa |
 * staging`), so the value maps directly and nothing is invented to fill it.
 */
export function reportPreviewSessionOutcome(
  client: ApiClient,
  outcome: PreviewSessionOutcome,
): void {
  void client
    .POST("/v1/observability/telemetry/preview-session", { body: outcome })
    .catch(() => undefined);
}

/**
 * The telemetry allowlist, as a value the mapping below can be checked against.
 *
 * `PreviewSessionOutcome.errorCode` is a closed enum in the contract, so a code
 * this application invented would be refused by the service rather than stored.
 * Narrowing through this set is what makes that a compile-time and run-time
 * fact rather than a comment.
 */
const TELEMETRY_ERROR_CODES = [
  "timeout",
  "validation_failed",
  "upstream_unavailable",
  "unknown",
] as const;

type TelemetryErrorCode = (typeof TELEMETRY_ERROR_CODES)[number];

function allowlistedErrorCode(code: string): TelemetryErrorCode {
  return (TELEMETRY_ERROR_CODES as readonly string[]).includes(code)
    ? (code as TelemetryErrorCode)
    : "unknown";
}

/**
 * The outcome and error code a refused session is reported as (#262).
 *
 * #261 reported `created` and left the failures here, because what each of them
 * MEANS is this ticket's question. The answer, per the owner's 2026-08-17
 * decision: report them through the allowlist the contract already has, and do
 * not widen it. `error_code` therefore says less than the surface does — a
 * stale target and an expired candidate are both `validation_failed` — and the
 * per-state distinction stays in the Editor Service's own logs, which is where
 * the 2026-08-06 collapse ruling put it. A telemetry enum is a poor place to
 * relitigate that, and widening it would be a contract change on an
 * unauthenticated, rate-limited endpoint.
 *
 * `expired` is deliberately NEVER sent, although the enum offers it. The
 * browser cannot tell an expired candidate from one this Project never had:
 * both are `422` on `candidateId`, collapsed on purpose by the route that
 * answers them, and `classifySessionFailure` is honest about that in the name
 * `candidate_unusable`. A signal that guessed between the two would be worse
 * than one that declines to.
 *
 * Nothing derived here carries a patient, candidate or target identifier — the
 * failure classification is a kind, not a value, so there is nothing to redact.
 */
export function sessionFailureOutcome(failure: SessionFailure): {
  outcome: PreviewSessionOutcome["outcome"];
  errorCode: TelemetryErrorCode;
} {
  switch (failure.kind) {
    // The service refused the request it was given, and said which fact it
    // refused on. `rejected` is that, and it is the whole of what the enum can
    // express about these four.
    case "stale_target":
    case "preview_target":
    case "candidate_unusable":
    case "candidate_validator_mismatch":
    case "patient_gone":
      return { outcome: "rejected", errorCode: "validation_failed" };
    // Also a refusal the service made on the request it was given, and the
    // allowlist says nothing finer — the WHICH lives in the Editor Service's
    // and the target's own logs, which do not depend on the browser choosing
    // to report anything. Deliberately not `error`: nothing failed.
    case "admission_refused":
      return { outcome: "rejected", errorCode: "unknown" };
    // Also a refusal, and deliberately not dressed as anything else. No
    // allowlist member describes authorization, so `unknown` is the honest
    // one; the authoritative record is the security event the Editor Service
    // emits at its exception-handler choke point, which does not depend on the
    // browser choosing to report anything.
    case "denied":
      return { outcome: "rejected", errorCode: "unknown" };
    case "upstream":
    case "validator_pin_unavailable":
      return { outcome: "error", errorCode: "upstream_unavailable" };
    case "unavailable":
      return { outcome: "error", errorCode: "unknown" };
    case "error":
      return { outcome: "error", errorCode: allowlistedErrorCode(failure.code) };
  }
}

/**
 * Why a preview session was not created.
 *
 * Kinds only, exactly as `FreezeFailure` above: the sentences live with the
 * surface that renders them, keyed by these members with `satisfies`, so a new
 * kind fails the build rather than rendering blank.
 */
export type SessionFailure =
  /** 409 `stale_preview_target_revision` — someone retargeted the Project. */
  | { kind: "stale_target"; currentRevision: number | undefined }
  /**
   * 409 `preview_admission_refused` — the deployment target's own service
   * applied contract §8 and refused this candidate's frozen safety posture
   * before any session existed (liv1.0#1216).
   *
   * BOTH reasons are carried and neither is derived from the other: the first
   * says which KIND of refusal (an incompatible posture, a candidate posture
   * the Editor publishes none for, a target that moved after the decision) and
   * the second, present only inside an incompatibility, says which BINDING
   * failed. `undefined` on either is a refusal whose reason this build cannot
   * name — still terminal, and never rendered as a guess.
   */
  | {
      kind: "admission_refused";
      admissionReason: string | undefined;
      refusalReason: string | undefined;
    }
  /** 422 `preview_target_not_enabled` — the target lost its credential. */
  | { kind: "preview_target" }
  /** 422 `validation_failed` on `candidateId` — unknown or expired. */
  | { kind: "candidate_unusable" }
  /** 422 `candidate_validator_mismatch` — immutable version must be rebuilt on this deployment. */
  | { kind: "candidate_validator_mismatch" }
  /** 422 `validation_failed` on `patientRef` — the fixture is no longer offered. */
  | { kind: "patient_gone" }
  | { kind: "denied" }
  /** 501 — the endpoint is not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  /** 502/503 — the preview target could not be reached or is not serving. */
  | { kind: "upstream" }
  /** 503 `validator_pin_unavailable` — operator-only deployment repair. */
  | { kind: "validator_pin_unavailable" }
  /** Anything else, carrying the machine-readable code and the request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

/**
 * Classifies a failed session by what came back, not by status alone.
 *
 * Two 422s share a status and mean different things, and the contract says how
 * to tell them apart: `error.field` names `candidateId` (no such candidate in
 * this Project, it has expired, or another Brain build validated it — freeze a new one) or `patientRef` (the
 * fixture is no longer offered — reload the list), while
 * `preview_target_not_enabled` names `previewTenantId`. Reading the field is
 * the contract's own instruction, not an inference from the message.
 *
 * `currentPreviewTargetRevision` is carried through, and #262 is what finally
 * reads it: the launch surface names the revision it decided against against
 * the one the service reports, which is the fact a clinician needs to re-decide
 * and the reason #261 kept the value rather than dropping it. It stays
 * `undefined`-tolerant, because the envelope is data: a 409 without the field
 * still refuses the launch, and the copy then names only what it has.
 */
export function classifySessionFailure(error: unknown): SessionFailure {
  if (error instanceof ApiError) {
    // BEFORE `isConflict`, and that order is the whole point: two codes now
    // share the 409, and the generic arm below means "someone retargeted this
    // Project" — a recovery that has nothing to do with a safety refusal.
    // Reading the code rather than the status is the contract's own
    // instruction, exactly as it is for the two 422s further down.
    if (error.code === "preview_admission_refused") {
      const admissionReason = error.envelope["previewAdmissionReason"];
      const refusalReason = error.envelope["finalQuestioningRefusalReason"];
      return {
        kind: "admission_refused",
        admissionReason:
          typeof admissionReason === "string" ? admissionReason : undefined,
        refusalReason:
          typeof refusalReason === "string" ? refusalReason : undefined,
      };
    }
    if (error.code === "candidate_validator_mismatch") {
      return { kind: "candidate_validator_mismatch" };
    }
    if (error.code === "validator_pin_unavailable") {
      return { kind: "validator_pin_unavailable" };
    }
    if (error.isConflict) {
      const current = error.envelope["currentPreviewTargetRevision"];
      return {
        kind: "stale_target",
        currentRevision: typeof current === "number" ? current : undefined,
      };
    }
    if (error.code === "preview_target_not_enabled") {
      return { kind: "preview_target" };
    }
    if (error.isForbidden || error.isUnauthenticated) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.status === 422) {
      const field = error.envelope["field"];
      if (field === "patientRef") {
        return { kind: "patient_gone" };
      }
      // `candidateId` and anything else the route can refuse on: the candidate
      // is the only other input the browser chose, and freezing a new one is
      // the recovery either way.
      return { kind: "candidate_unusable" };
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return { kind: "upstream" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // A thrown non-ApiError is a network or runtime failure: no status, no code.
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}

/**
 * Why the fixture listing could not be read.
 *
 * A smaller union than `SessionFailure` because the route offers fewer
 * distinctions, and deliberately so: the contract collapses THREE upstream
 * conditions — a missing credential, a refused one, and a preview-disabled
 * environment — into one `503 upstream_unavailable`, "because nothing in the
 * browser would act differently". Inventing three sentences here would be this
 * application claiming a distinction the envelope refuses to make.
 */
export type PatientListingFailure =
  /** 502/503 — the preview target is unreachable or is not serving previews. */
  | { kind: "upstream" }
  | { kind: "denied" }
  /** 501 — the endpoint is not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  | { kind: "error"; code: string; requestId: string | undefined };

export function classifyPatientListingFailure(
  error: unknown,
): PatientListingFailure {
  if (error instanceof ApiError) {
    if (error.isForbidden || error.isUnauthenticated) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return { kind: "upstream" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}

/**
 * Moves the Project's preview target, compare-and-set (#338).
 *
 * The whole request is the path `projectId` plus `ChangePreviewTargetRequest`.
 * The revision travels WITH the choice rather than being resolved inside this
 * hook: the value that must be sent is the one the surface DISPLAYED when the
 * clinician chose, and a hook that re-read it from cache would quietly re-state
 * a revision that had moved — the same defect `ProjectPreviewRoute`'s revision
 * effect exists to prevent on the launch path.
 *
 * `retry: false`. A retry is never a no-op here: the contract says every
 * ACCEPTED change increments the revision, "including a change to the target the
 * Project already has", so an automatic second attempt would either bump the
 * revision twice or race its own first attempt into the 409 that exists to
 * report somebody else's change.
 */
export function useChangePreviewTargetMutation(
  projectId: string,
): UseMutationResult<ProjectSummary, unknown, ChangePreviewTargetRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body: ChangePreviewTargetRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/preview-target", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    onSuccess(summary) {
      /*
       * The revision that gets DISPLAYED is the one this response carried.
       *
       * The contract is explicit that "the value to send next is the one in the
       * response, not the one you sent", and the acceptance criterion says the
       * displayed revision must be the server-returned one rather than a local
       * increment. Writing the returned `ProjectSummary` into the overview's
       * cache is what makes that literally true: the number on screen is bytes
       * the service sent, not arithmetic this application did — and not a value
       * a follow-up read might race a second retarget into.
       *
       * Moving it here is also what clears the patient. `ProjectPreviewRoute`'s
       * revision effect watches exactly this field and drops a `patientRef`
       * chosen against the old target; nothing in this file clears a selection,
       * because a second mechanism for that would be a defect rather than belt
       * and braces (PR #335 review, and #338's clarifications).
       */
      queryClient.setQueryData<ProjectOverview>(
        queryKeys.projectOverview(projectId),
        (current) => (current ? { ...current, project: summary } : current),
      );
      invalidateProjectHistory(queryClient, projectId);
      /*
       * Written, and NOT followed by an OVERVIEW invalidation, which is the
       * deliberate half. History is a separate projection and is invalidated
       * above because this write committed its preview-target-change row.
       *
       * The obvious belt-and-braces version splices and then re-reads. It also
       * quietly undoes the criterion: the refetch resolves a moment later and
       * whatever IT returns is what stays on screen, so "the displayed revision
       * is the server-returned one" would hold for one tick and then become
       * "whatever a second read said". There is nothing to reconcile anyway —
       * `ProjectOverview.project` is the whole of what a retarget changes, and
       * the route responds to the moved revision by invalidating the
       * target-scoped patient list itself.
       */
    },
    retry: false,
  });
}

/**
 * Why a retarget was refused.
 *
 * Kinds only, exactly as `FreezeFailure` and `SessionFailure` above: the
 * sentences live with the surface that renders them, keyed by these members with
 * `satisfies`, so a new kind fails the build rather than rendering blank.
 *
 * `preview_target` is separated from everything else because it is the one
 * refusal that belongs on a FIELD. The contract sets `error.field` to
 * `previewTenantId` for it, which is the service saying "the value you chose",
 * not "the request you made" — and #338's acceptance criterion asks for exactly
 * that attribution rather than a generic failure.
 */
export type RetargetFailure =
  /** 422 `preview_target_not_enabled` — no credential for the chosen target. */
  | { kind: "preview_target" }
  /** 409 `stale_preview_target_revision` — somebody else retargeted first. */
  | { kind: "stale_target"; currentRevision: number | undefined }
  | { kind: "denied" }
  /** 501 — the endpoint is not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  /** Anything else, carrying the machine-readable code and the request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

/**
 * Classifies a failed retarget by what came back, not by status alone.
 *
 * The 422 is read from `error.code` rather than from the status, because this
 * route can also answer 422 for a request the contract refuses outright, and the
 * two have different recoveries — re-read the listing, or fix the request.
 *
 * `currentPreviewTargetRevision` is carried through and, since #262, read: the
 * refusal names the revision this form sent against the one the service holds.
 * What it still does NOT do is act on it — no target is proposed and none is
 * prefilled, because the recovery is the clinician's decision to make against a
 * refreshed display, not a resend the browser assembles for them.
 */
export function classifyRetargetFailure(error: unknown): RetargetFailure {
  if (error instanceof ApiError) {
    if (error.code === "preview_target_not_enabled") {
      return { kind: "preview_target" };
    }
    if (error.isConflict) {
      const current = error.envelope["currentPreviewTargetRevision"];
      return {
        kind: "stale_target",
        currentRevision: typeof current === "number" ? current : undefined,
      };
    }
    if (error.isForbidden || error.isUnauthenticated) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // A thrown non-ApiError is a network or runtime failure: no status, no code.
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}
